import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { SelectItem, ConfirmationService, FileUpload, MessageService } from 'primeng';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { IUser } from '../../interfaces/user';
import { OrganisationsService } from '../../services';
import { RoleService } from '../../services';
import { UserService } from '../../services';
import { getCombinedParameters } from '../../utils/getCombinedParameters';
import { tryParseInt } from '../../utils/tryParseInt';
import { OrgTreeComponent } from './org-tree.component';
import { Message } from '../../classes/Message';
import { DomSanitizer } from '@angular/platform-browser';
import { SupplierTravelPreferencesService } from '../../services/supplier-travel-preferences.service';

@Component({
  selector: 'new-user-details',
  templateUrl: 'new-user-details.component.html',
  styleUrls: ['./new-user-details.component.scss'],
})
export class NewUserDetailsComponent implements OnInit, AfterViewInit {
  //@ViewChild(UsersComponent) userDetails: UsersComponent

  public isBusy: boolean = false;
  public orgType: number;
  public userOrgTypeId: number;
  public userId: string;
  public oldUser: IUser = null;
  public isNew: boolean = true;
  public orgId: number;
  public backTo: string;
  public userForm: UntypedFormGroup;

  public roles: SelectItem[] = [];
  public organisations: SelectItem[] = [];
  public organisationDatas: any[] = [];
  public departments: SelectItem[] = [];
  public regions: SelectItem[] = [];
  public teams: SelectItem[] = [];
  public depots: SelectItem[] = [];
  public serviceDeliveryGroups: SelectItem[] = [];

  public selectedOrgData: any = null;
  public selectedOrg: any = null;
  public selectedRoles: any[] = [];
  public selectedDepartment: any = null;
  public selectedRegion: any = null;
  public selectedTeam: any[] = [];
  public selectedDepot: any[] = [];
  public selectedDefaultTeam: any;
  public selectedDefaultDepot: any;
  public selectedTeams: any[];
  public selectedDepots: any[];
  public isAllTeams: boolean = false;
  public isAllDepots: boolean = false;

  public confirmDlg: boolean = false;
  public confirmsg: string = '';

  public enableImpersonateBtn: boolean = false;
  public hideImpersonateBtn: boolean = true;

  public disableTwoFactorRequiredField: boolean = false;

  // Users can't change their own roles unless they're a portal admin (POAD)
  public cannotEditOwnRoles: boolean = false;

  // Org Types
  private OrgType_VME = 1;
  private OrgType_Customer = 2;
  private OrgType_Supplier = 3;
  private OrgType_CustomerAndSupplier = 4;

  isVmeUser = false;

  public uploadedFileSrc: string;

  @ViewChild('orgTreeCustomer') orgTreeCustomer: OrgTreeComponent;
  @ViewChild('orgTreeSupplier') orgTreeSupplier: OrgTreeComponent;

  @ViewChild('pUploader') pUploader: FileUpload;
  profileImageUploaded: boolean;

  travelPreferencesEnabled = false;

  constructor(
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private orgService: OrganisationsService,
    private roleService: RoleService,
    private location: Location,
    private confirmationService: ConfirmationService,
    public messageService: MessageService,
    private sanitizer: DomSanitizer,
    private organisationService: OrganisationsService,
    private travelPreferencesService: SupplierTravelPreferencesService,
  ) {}

  ngOnInit() {
    this.confirmDlg = false;
    this.confirmsg = '';

    // this.pUploader.files = [new File([new Blob()], "test.jpg", {type: 'image/jpeg'})]

    getCombinedParameters(this.route).subscribe((params) => {
      this.userId = params['id'];
      this.isNew = !this.userId;
      this.orgId = params['orgId'];
      this.backTo = params['backTo'];
      this.orgType = tryParseInt(params['orgTypeId']) as number;

      this.fetchUser().then(() => {
        this.loadServiceGroups();
        this.loadOrganisations();
      });

      this.userForm = this.formBuilder.group({
        email: [
          '',
          Validators.compose([Validators.required, Validators.email, Validators.maxLength(254)]),
        ],
        sendOrderConfirmationEmail: [true, Validators.compose([Validators.required])],
        phoneNumber: ['', Validators.compose([Validators.required, Validators.maxLength(20)])],
        firstName: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
        lastName: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
        userRoles: [[], Validators.required],
        organisationId: [null, Validators.required],
        twoFactorRequired: [this.orgType == 1],
        jobTitle: ['', Validators.maxLength(30)],
        profileImageUrl: [null],
        initials: [null],
        serviceDeliveryGroupId: [null],
        //userOrganisationLevel: this.formBuilder.group({
        //    id: [0],
        //    userId: [null],
        //    organisationId: [null],
        //    supplierRegionId: [null],
        //    supplierDepotId: [null],
        //    customerDepartmentId: [null],
        //    customerTeamId: [null],
        //})
      });

      // todo: check this, previously type was coerced and likely never worked.
      if (this.orgType !== 1) {
        this.userService.getLoggedUser().then((res) => {
          this.userForm.patchValue({
            organisationId: res.organisationId,
          });

          this.orgId = res.organisationId;
        });
      } else {
        this.userForm.patchValue({
          organisationId: this.orgId,
        });
      }
    });

    this.travelPreferencesService
      .isTravelPreferencesEnabled()
      .then((enabled) => (this.travelPreferencesEnabled = enabled))
      .catch(() => (this.travelPreferencesEnabled = false));
  }

  fetchUser() {
    // Fetch the org from the user
    if (this.orgId == null && this.userId != null && this.orgType == 1) {
      return this.userService.get(this.userId).then((data) => {
        this.orgId = data.organisationId;
        this.userOrgTypeId = data.organisation.organisationTypeId;

        //If the user currently being edited is a VME user proceed
        if (this.userOrgTypeId == 1) {
          //Check if the user logged in has the POAD role - if they do then set the disable flag to 'False'.
          this.userService.isUserInRole('POAD').then((res) => {
            this.disableTwoFactorRequiredField = !res;
          });
        }
      });
    }

    return Promise.resolve();
  }

  ngAfterViewInit() {}

  onBeforeProfileImageUpload(event) {
    event.formData.append('userId', this.userId);

    // this is causing an error due to a primeNG and ng version mismatch,
    // so it fails to properly prepare the request - xhr is not present on the event.
    // event.xhr.responseType = 'json';
  }

  onSelectProfileImage(event) {
    this.getImageSource(this.pUploader.files[0]).then((src) => {
      this.uploadedFileSrc = src;
    });
  }

  onClearProfileImage() {
    this.resetProfileImage();
  }

  onProfileImageUploadError(event) {
    this.messageService.add(Message.Error('<div>Profile image could not be uploaded.</div>'));
  }

  onProfileImageUpload(event) {
    this.save(this.userForm.value);
  }

  saveForm(formValues) {
    if (this.isNew) {
      this.invite(formValues);
    } else if (this.profileImageChanged()) {
      // The call to upload() does not return a promise so instead
      // onUpload callback calls save()
      this.pUploader.upload();
    } else {
      this.save(formValues);
    }
  }

  save(formValues) {
    var user = <IUser>formValues;
    this.getSelectedTreeItems(user);

    this.userService.save(user).then((data) => {
      if (typeof data !== undefined) {
        this.cancel();
      }
    });
  }

  invite(formValues) {
    var user = <IUser>formValues;
    this.getSelectedTreeItems(user);

    user.organisationId = this.selectedOrgData.id;
    //user.userOrganisationLevel.organisationId = this.selectedOrgData.id;

    this.userService.inviteUser(user).then(
      (data) => {
        if (data.succeeded) {
          this.confirmsg =
            'The invitation has been sent. You will be redirected to the users list page';
          this.confirmDlg = true;
        }
      },
      (error) => {
        var errMsg = 'Error';

        if (error.error !== undefined) {
          errMsg = error.error.Message;
        }

        this.messageService.add(Message.Error(errMsg));
      },
    );
  }

  getSelectedTreeItems(user: IUser) {
    if (this.orgTreeCustomer) {
      if (this.isAllTeams) {
        user.teamId = [{ isAll: true }];

        if (this.selectedDefaultTeam) {
          user.teamId.push({ id: this.selectedDefaultTeam, isAll: false, isDefault: true });
        }
      } else {
        user.teamId = [];
        const data = this.orgTreeCustomer.getSelectedIds();
        data.forEach((x) =>
          user.teamId.push({ id: x, isAll: false, isDefault: this.selectedDefaultTeam == x }),
        );
      }
    }

    if (this.orgTreeSupplier) {
      if (this.isAllDepots) {
        user.depotId = [{ isAll: true }];

        if (this.selectedDefaultDepot) {
          user.depotId.push({ id: this.selectedDefaultDepot, isAll: false, isDefault: true });
        }
      } else {
        user.depotId = [];
        var data = this.orgTreeSupplier.getSelectedIds();
        data.forEach((x) =>
          user.depotId.push({ id: x, isAll: false, isDefault: this.selectedDefaultDepot == x }),
        );
      }
    }
  }

  resendInvitation() {
    var userId = this.userId;
    this.userService
      .resendInvitation({ id: userId })
      .catch(() => this.messageService.add(Message.Error('The invitation could not be resent.')));
  }

  goToUsersList() {
    this.confirmDlg = false;
    if (this.backTo) {
      this.router.navigateByUrl(this.backTo);
      return;
    }

    if (this.orgType == 1) this.router.navigate(['app/vmeusers']);
    else if (this.orgType == 2) this.router.navigate(['app/customerusers']);
    else if (this.orgType == 3) this.router.navigate(['app/supplierusers']);
    else if (this.orgType == 5) this.router.navigate(['app/saasusers']);
    else this.location.back();
  }

  cancel() {
    if (this.backTo) {
      this.router.navigateByUrl(this.backTo);
      return;
    }

    if (this.orgType == 1) this.router.navigate(['app/vmeusers']);
    else if (this.orgType == 2) this.router.navigate(['app/customerusers']);
    else if (this.orgType == 3) this.router.navigate(['app/supplierusers']);
    else if (this.orgType == 5) this.router.navigate(['app/saasusers']);
    else this.location.back();
  }

  resetProfileImage() {
    this.pUploader.files = [new File([new Blob()], 'existing', { type: 'image/jpeg' })];
  }

  loadOrganisations() {
    this.isBusy = true;

    this.organisationDatas = [];

    if (this.orgType == 1) {
      var loadOrganisationFn = this.orgService
        .getAllOrg({ filters: { id: { value: this.orgId } } })
        .then((data) => {
          this.organisations = [];
          this.organisations.push({ label: 'Select Organisation...', value: null });
          this.selectedOrg = null;
          this.selectedOrgData = null;
          data.forEach((o) => {
            this.organisationDatas.push(o);
            this.organisations.push({ label: o.name, value: o.id });
          });
        });
    } else {
      var loadOrganisationFn = this.userService.getCurrentUserOrg().then((data) => {
        this.organisations = [];
        this.selectedOrgData = data;
        this.organisationDatas.push(data);
        this.organisations.push({ label: data.name, value: data.id });
        this.selectedOrg = data.id;
      });
    }

    loadOrganisationFn
      .then((data) => {
        this.isBusy = false;
        if (!this.isNew) {
          this.isBusy = true;
          let calls = [
            this.userService.get(this.userId).then((data) => {
              this.resetProfileImage();
              this.userForm = this.formBuilder.group({
                id: [data.id],
                email: [
                  data.email,
                  Validators.compose([
                    Validators.required,
                    Validators.email,
                    Validators.maxLength(254),
                  ]),
                ],
                sendOrderConfirmationEmail: [
                  data.sendOrderConfirmationEmail,
                  Validators.compose([Validators.required]),
                ],
                phoneNumber: [
                  data.phoneNumber,
                  Validators.compose([Validators.required, Validators.maxLength(20)]),
                ],
                firstName: [
                  data.firstName,
                  Validators.compose([Validators.required, Validators.maxLength(100)]),
                ],
                lastName: [
                  data.lastName,
                  Validators.compose([Validators.required, Validators.maxLength(100)]),
                ],

                userRoles: [data.userRoles, Validators.required],
                organisationId: [data.organisationId, Validators.required],
                twoFactorRequired: [data.twoFactorRequired],
                profileImageUrl: [data.profileImageUrl],
                initials: [data.initials],
                jobTitle: [data.jobTitle],
                serviceDeliveryGroupId: [data.serviceDeliveryGroupId],
                //userOrganisationLevel: this.formBuilder.group({
                //    id: [data.userOrganisationLevel.id],
                //    userId: [data.userOrganisationLevel.userId],
                //    organisationId: [data.userOrganisationLevel.organisationId],
                //    supplierRegionId: [data.userOrganisationLevel.supplierRegionId],
                //    supplierDepotId: [data.userOrganisationLevel.supplierDepotId],
                //    customerDepartmentId: [data.userOrganisationLevel.customerDepartmentId],
                //    customerTeamId: [data.userOrganisationLevel.customerTeamId],
                //})
              });

              this.oldUser = data;

              this.lockOwnRoles();

              this.organisationDatas.forEach((o) => {
                if (o.id == data.organisationId) {
                  this.selectedOrgData = o;
                }
              });
              this.selectedOrg = data.organisationId;

              this.isVmeUser = data.organisation.organisationTypeId === 1;

              this.loadRoles(data.userRoles);

              this.selectedTeam = data.teamId;
              this.selectedDepot = data.depotId;
              this.profileImageUploaded = data.profileImageUploaded;

              this.isAllTeams = data.teamId.filter((x) => x.isAll).length != 0;
              this.isAllDepots = data.depotId.filter((x) => x.isAll).length != 0;

              const defaultTeam = data.teamId.find((x) => x.isDefault);
              if (defaultTeam) {
                this.selectedDefaultTeam = defaultTeam.id;
              }

              const defaultDepot = data.depotId.find((x) => x.isDefault);
              if (defaultDepot) {
                this.selectedDefaultDepot = defaultDepot.id;
              }

              //this.setUserRoles(data.roles);

              //this.loadDepartments();
              //this.loadTeams();

              //this.loadRegions();
              //this.loadDepots();
            }),
          ];
          if (this.canImpersonate()) {
            this.hideImpersonateBtn = false;
            calls.push(
              this.userService.canImpersonate(this.userId).then((res: boolean) => {
                this.enableImpersonateBtn = res;
              }),
            );
          }
          Promise.all(calls).then(() => (this.isBusy = false));
        } else if (this.selectedOrgData && this.selectedOrgData.organisationTypeId == 2) {
          this.loadRoles(null);
          //this.loadDepartments();
        } else if (this.selectedOrgData && this.selectedOrgData.organisationTypeId == 3) {
          this.loadRoles(null);
          //this.loadRegions();
        } else if (this.selectedOrgData && this.selectedOrgData.organisationTypeId == 4) {
          this.loadRoles(null);
          //this.loadDepartments();
          //this.loadRegions();
        } else if (this.selectedOrgData && this.selectedOrgData.organisationTypeId == 5) {
          this.loadRoles(null);
        }

        //Load selected organisation if this page has come from organisations page
        if (this.isNew && this.orgId != null && this.orgId.toString() != 'undefined') {
          this.selectedOrg = this.orgId;

          if (this.selectedOrgData == null && this.orgType == 1) {
            this.organisationDatas.forEach((o) => {
              if (o.id == this.selectedOrg) {
                this.selectedOrgData = o;
              }
            });
            this.loadRoles(null);
            this.loadDepartments();
            this.loadRegions();
          }
        }
      })
      .catch((ex) => {
        this.isBusy = false;
        this.messageService.add({
          severity: 'error',
          summary: '',
          detail: 'Error while loading data, try again or contact the administrator.',
        });
      });
  }

  setUserRoles(savedRoles) {
    this.selectedRoles = savedRoles;
  }

  loadDepartments() {
    this.departments = [];
    this.departments.push({ label: 'Select Department...', value: null });
    this.selectedDepartment = null;

    this.selectedOrgData.departments.forEach((d) => {
      this.departments.push({ label: d.name, value: d.id });
    });

    if (this.oldUser && this.oldUser.userOrganisationLevel) {
      this.selectedDepartment = this.oldUser.userOrganisationLevel.customerDepartmentId;
    }
  }

  loadRegions() {
    this.regions = [];
    var defRegion = { label: 'Select Region...', value: null };
    this.regions.push(defRegion);
    this.selectedRegion = null;

    this.selectedOrgData.regions.forEach((d) => {
      var region = { label: d.name, value: d.id };
      this.regions.push(region);
    });

    if (this.oldUser && this.oldUser.userOrganisationLevel) {
      this.selectedRegion = this.oldUser.userOrganisationLevel.supplierRegionId;
    }
  }

  /*
  loadTeams() {
    this.teams = [];
    this.selectedTeam = null;
    if (this.selectedDepartment != null) {
      this.teams.push({label: 'Select Team...', value: null});
      this.orgService.getDepartment(this.selectedDepartment).then((data) => {
        if (data != null) {
          data.teams.forEach((t) => {
            var team = {label: t.name, value: t.id};
            this.teams.push(team);
          });
        }

        if (this.oldUser && this.oldUser.userOrganisationLevel) {
          //this.selectedTeam = this.oldUser.userOrganisationLevel.customerTeamId;
        }
      });
    }
  }

  loadDepots() {
    this.depots = [];
    this.selectedDepot = null;
    if (this.selectedRegion != null) {
      var defDepots = {label: 'Select Depot...', value: null};
      this.depots.push(defDepots);

      this.orgService.getRegion(this.selectedRegion).then((data) => {
        if (data != null) {
          data.depots.forEach((d) => {
            var depot = {label: d.name, value: d.id};
            this.depots.push(depot);
          });
        }

        if (this.oldUser && this.oldUser.userOrganisationLevel) {
          //this.selectedDepot = this.oldUser.userOrganisationLevel.supplierDepotId;
        }
      });
    }
  }

   */

  loadServiceGroups() {
    this.serviceDeliveryGroups = [];

    this.serviceDeliveryGroups = [{ label: 'None', value: null }];
    this.organisationService.getServiceDeliveryGroups().then((result) => {
      result.forEach((item) => {
        this.serviceDeliveryGroups.push({ label: item.name, value: item.id });
      });
    });
  }

  rolesChange() {}

  loadRoles(userRoles) {
    if (this.isNew) {
      this.selectedRoles = [];
      if (this.userForm) {
        this.userForm.controls['userRoles'].reset();
      }
    }

    this.roleService.getRolesByOrg(this.selectedOrgData.id, this.orgType).then((data) => {
      this.roles = [];
      data.forEach((r) => {
        this.roles.push({ label: r.description, value: r });
      });

      //Fill roles from user data
      if (!this.isNew && userRoles != null) {
        this.setUserRoles(userRoles);
      }
    });
  }

  /* Not used, commented out
  orgChange($event) {
    this.selectedOrgData = null;
    this.organisationDatas.forEach((o) => {
      if (o.id == $event.value) {
        this.selectedOrgData = o;
      }
    });

    if (this.selectedOrgData == null) {
      return;
    }

    this.loadRoles(null);
    this.loadDepartments();
    this.loadRegions();
  }


  departmentChange($event) {
    this.loadTeams();
  }

  regionChange($event) {
    this.loadDepots();
  }

   */

  impersonate() {
    var obj = {
      ImpersonatedUserGUID: this.userId,
      ImpersonatedUserName: '',
      OriginalUserName: '',
      IsImpersonating: false,
    };
    this.userService.impersonateUser(obj).then((res) => {
      window.location.reload();
      this.router.navigate(['/app']);
    });
  }

  profileImageChanged(): boolean {
    return (
      this.pUploader &&
      this.pUploader.files.length > 0 &&
      this.pUploader.files[0].name != 'existing'
    );
  }

  private getImageSource(file: File): Promise<string> {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = (e) => res(reader.result as string);
      reader.onerror = (e) => rej(e);
      reader.readAsDataURL(file);
    });
  }

  private lockOwnRoles() {
    const currentUser = this.userService.currentUser;
    const isPortalAdmin =
      !!currentUser && currentUser.roles.some((x) => x.isBelong && x.name === 'POAD');

    this.cannotEditOwnRoles =
      !this.isNew && !!currentUser && currentUser.id === this.userId && !isPortalAdmin;

    // A disabled control is left out of the form value, so no roles are sent on save
    if (this.cannotEditOwnRoles) {
      this.userForm.controls['userRoles'].disable();
    }
  }

  private canImpersonate(): boolean {
    let user = this.userService.currentUser;
    if (!user) return false;
    let roles = ['MANG', 'AADM', 'FINC', 'ACCM'];

    return user.roles.filter((x) => x.isBelong && roles.indexOf(x.name) !== -1).length > 0;
  }

  public getSelectedTeams() {}

  public isAllTeamsChange(event) {
    if (this.orgTreeCustomer) {
      if (this.isAllTeams) {
        this.orgTreeCustomer.selectAll();
      } else {
        this.orgTreeCustomer.deselectAll();
      }
    }
  }

  public isAllDepotsChange(event) {
    if (this.orgTreeSupplier) {
      if (this.isAllDepots) {
        this.orgTreeSupplier.selectAll();
      } else {
        this.orgTreeSupplier.deselectAll();
      }
    }
  }

  public selectedTeamsChange() {
    if (this.orgTreeCustomer) {
      this.selectedTeams = [{ label: 'Select Default Team...', value: null }];
      this.orgTreeCustomer.selectedItems.forEach((x) => {
        if (x.data.nodeType == 'Team') {
          this.selectedTeams.push({ label: x.data.name, value: x.data.nodeId });
        }
      });
    }
  }

  public selectedDepotsChange() {
    if (this.orgTreeSupplier) {
      this.selectedDepots = [{ label: 'Select Default Depot...', value: null }];
      this.orgTreeSupplier.selectedItems.forEach((x) => {
        if (x.data.nodeType == 'Depot') {
          this.selectedDepots.push({ label: x.data.name, value: x.data.nodeId });
        }
      });
    }
  }

  get isTeamSelectionInvalid(): boolean {
    // Check if the selected organisation type is valid (2 = Customer || 4 = Both)
    const hasValidOrgType =
      this.selectedOrgData &&
      (this.selectedOrgData.organisationTypeId == 2 ||
        this.selectedOrgData.organisationTypeId == 4);

    // Check if there are any items in the selected orgTree with a nodeType 'Team'
    const noSelectedTeam =
      this.orgTreeCustomer &&
      this.orgTreeCustomer.selectedItems.filter((item) => item.data.nodeType == 'Team').length == 0;

    // Return true if we have a valid org type and no selected team
    return hasValidOrgType && noSelectedTeam;
  }

  deleteImage() {
    this.userService.deleteProfileImage(this.userId).then((data) => {
      this.profileImageUploaded = false;
    });
  }
}
